import { create } from "zustand";
import {
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import missionsData from "../../components/StoryMode/missions.json";
import {
  advanceStoryProgress,
  buildInitialStoryProgress,
  getCurrentStoryBeat,
  resolveStoryProgress,
  STORY_TARGET_KINDS,
  withStoryCutsceneProgress,
  withStoryLaunchTarget,
} from "../utils/storyProgression";
import {
  buildInitialStoryState,
  getStoryInventoryDocId,
  normalizeStoryInventory,
  normalizeStoryInventoryEntry,
  resolveStoryState,
} from "../utils/storyState";
import {
  getStoryMerchantCatalogItem,
  hasCompletedStoryMerchantUnlock,
} from "../utils/storyMerchant";
import { db } from "../firebase/config";

const storyProgressRefForUser = (userId) =>
  doc(db, "users", userId, "story", "progress");
const storyStateRefForUser = (userId) =>
  doc(db, "users", userId, "story", "data");
const storyInventoryCollectionForUser = (userId) =>
  collection(db, "users", userId, "story", "data", "inventory");
const storyInventoryItemRefForUser = (userId, itemDocId) =>
  doc(db, "users", userId, "story", "data", "inventory", itemDocId);

const stripUndefinedDeep = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;

  if (Array.isArray(value)) {
    return value
      .map((entry) => stripUndefinedDeep(entry))
      .filter((entry) => entry !== undefined);
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      const sanitized = stripUndefinedDeep(entry);
      if (sanitized !== undefined) {
        acc[key] = sanitized;
      }
      return acc;
    }, {});
  }

  return value;
};

const sanitizeProgressForFirestore = (progress = {}) => ({
  ...stripUndefinedDeep(progress),
  updatedAt: serverTimestamp(),
  startedAt: progress.startedAt ?? serverTimestamp(),
});

const sanitizeStoryStateForFirestore = (storyState = {}) => ({
  ...stripUndefinedDeep(resolveStoryState(storyState)),
  updatedAt: serverTimestamp(),
  startedAt: storyState.startedAt ?? serverTimestamp(),
});

const sanitizeStoryInventoryItemForFirestore = (item = {}) =>
  Object.entries(item).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});

const STORY_STATE_REQUIRED_KEYS = [
  "infinityTokens",
  "level",
  "xp",
  "pendingBoostAllocations",
  "storyCharacterBoosts",
  "storyXpClaims",
  "storyBattleXpClaims",
  "storyBattleResolutionClaims",
  "inventoryCount",
  "packUnlocked",
  "storyTeamUnlocked",
  "storyTeamRoster",
  "activeStoryTeamRoster",
  "activeStoryAvatarKey",
  "storyCompanionUnlocked",
  "storyCompanionRoster",
  "activeStoryCompanionId",
  "storyMissionEncounterProgress",
  "activeStorySideQuestId",
  "storySideQuestProgress",
  "turnedInStorySideQuestIds",
  "voidCaveUnlocked",
  "runeStoneTravelUnlocked",
  "startedAt",
  "updatedAt",
];

const needsStoryStateMerge = (rawStoryState = {}) =>
  STORY_STATE_REQUIRED_KEYS.some(
    (key) => !Object.prototype.hasOwnProperty.call(rawStoryState || {}, key),
  );

const getInventoryTotal = (inventory = []) =>
  inventory.reduce((total, item) => total + (Number(item?.quantity) || 0), 0);

const createStoryMerchantError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const migrateInlineInventoryToSubcollection = async (
  userId,
  rawStoryState = {},
) => {
  if (!Array.isArray(rawStoryState?.inventory)) return false;

  const inventory = normalizeStoryInventory(rawStoryState.inventory);
  const batch = writeBatch(db);
  const stateRef = storyStateRefForUser(userId);

  inventory.forEach((item) => {
    const itemDocId = getStoryInventoryDocId(item);
    if (!itemDocId) return;

    batch.set(
      storyInventoryItemRefForUser(userId, itemDocId),
      sanitizeStoryInventoryItemForFirestore({
        ...item,
        updatedAt: serverTimestamp(),
        acquiredAt: item.acquiredAt ?? serverTimestamp(),
      }),
      { merge: true },
    );
  });

  batch.set(
    stateRef,
    {
      ...sanitizeStoryStateForFirestore({
        ...rawStoryState,
        inventoryCount: getInventoryTotal(inventory),
      }),
      inventory: deleteField(),
    },
    { merge: true },
  );

  await batch.commit();
  return true;
};

const applyResolvedProgress = (set, storyData, progress) => {
  const beat = getCurrentStoryBeat(storyData, progress);
  const currentLaunchTarget = progress.currentLaunchTarget || null;

  set({
    progress: {
      ...progress,
      currentLaunchTarget,
    },
    currentAct: progress.currentAct ?? progress.actNumber ?? beat.mission?.actNumber ?? 1,
    currentMission: progress.currentMission ?? beat.mission?.missionNumber ?? 1,
    currentMissionId: progress.currentMissionId ?? beat.mission?.missionId ?? null,
    currentStepIndex: progress.currentStepIndex ?? beat.stepIndex ?? 0,
    currentLaunchTarget,
  });
};

export const useStoryStore = create((set, get) => ({
  isReady: false,
  storyData: missionsData,
  progress: null,
  storyState: buildInitialStoryState(),
  storyStateReady: false,
  storyInventory: [],
  storyInventoryReady: false,
  currentAct: 1,
  currentMission: 1,
  currentMissionId: null,
  currentStepIndex: 0,
  currentLaunchTarget: null,
  _progressWriteChain: null,
  _unsub: null,
  _unsubState: null,
  _unsubInventory: null,

  getCurrentMission: () => {
    if (!get().progress) return null;
    return getCurrentStoryBeat(get().storyData, get().progress).mission;
  },
  getCurrentStep: (progressOverride) => {
    const progress = progressOverride || get().progress;
    if (!progress) return null;
    return getCurrentStoryBeat(get().storyData, progress).step;
  },
  getCurrentLaunchTarget: () =>
    get().progress?.currentLaunchTarget || get().currentLaunchTarget || null,

  init: async (userId) => {
    if (!userId) return;

    get().cleanup();

    const progressRef = storyProgressRefForUser(userId);
    const stateRef = storyStateRefForUser(userId);
    const [progressSnap, stateSnap] = await Promise.all([
      getDoc(progressRef),
      getDoc(stateRef),
    ]);

    if (!progressSnap.exists()) {
      const initialProgress = buildInitialStoryProgress(get().storyData);
      await setDoc(progressRef, sanitizeProgressForFirestore(initialProgress), {
        merge: true,
      });
    } else {
      const { progress: migratedProgress, didMigrate } = resolveStoryProgress(
        get().storyData,
        progressSnap.data(),
      );
      if (didMigrate) {
        await setDoc(progressRef, sanitizeProgressForFirestore(migratedProgress), {
          merge: true,
        });
      }
    }

    if (!stateSnap.exists()) {
      const initialState = buildInitialStoryState();
      await setDoc(stateRef, sanitizeStoryStateForFirestore(initialState), {
        merge: true,
      });
    } else {
      const didMigrateInventory = await migrateInlineInventoryToSubcollection(
        userId,
        stateSnap.data(),
      );
      const resolvedState = resolveStoryState(stateSnap.data());
      if (needsStoryStateMerge(stateSnap.data()) && !didMigrateInventory) {
        await setDoc(
          stateRef,
          {
            ...sanitizeStoryStateForFirestore(resolvedState),
            inventory: deleteField(),
          },
          {
            merge: true,
          },
        );
      }
    }

    const unsub = onSnapshot(progressRef, (docSnap) => {
      if (!docSnap.exists()) return;

      const { progress } = resolveStoryProgress(get().storyData, docSnap.data());
      applyResolvedProgress(set, get().storyData, progress);
      set({ isReady: true });
    });

    const unsubState = onSnapshot(stateRef, (docSnap) => {
      if (!docSnap.exists()) return;

      set({
        storyState: resolveStoryState(docSnap.data()),
        storyStateReady: true,
      });
    });

    const unsubInventory = onSnapshot(
      storyInventoryCollectionForUser(userId),
      (snapshot) => {
        const storyInventory = snapshot.docs
          .map((inventoryDoc) => {
            const entry = normalizeStoryInventoryEntry(inventoryDoc.data());
            return entry ? { ...entry, docId: inventoryDoc.id } : null;
          })
          .filter(Boolean);
        const inventoryCount = getInventoryTotal(storyInventory);

        set((state) => ({
          storyInventory,
          storyInventoryReady: true,
          storyState: {
            ...state.storyState,
            inventoryCount,
          },
        }));
      },
    );

    set({
      _unsub: unsub,
      _unsubState: unsubState,
      _unsubInventory: unsubInventory,
    });
  },

  cleanup: () => {
    const unsub = get()._unsub;
    const unsubState = get()._unsubState;
    const unsubInventory = get()._unsubInventory;
    if (unsub) unsub();
    if (unsubState) unsubState();
    if (unsubInventory) unsubInventory();

    set({
      _unsub: null,
      _unsubState: null,
      _unsubInventory: null,
      isReady: false,
      progress: null,
      storyState: buildInitialStoryState(),
      storyStateReady: false,
      storyInventory: [],
      storyInventoryReady: false,
      currentAct: 1,
      currentMission: 1,
      currentMissionId: null,
      currentLaunchTarget: null,
      currentStepIndex: 0,
      _progressWriteChain: null,
    });
  },

  _readProgressForMutation: async (userId) => {
    if (!userId) return null;

    const currentProgress = get().progress;
    if (currentProgress) return currentProgress;

    const progressRef = storyProgressRefForUser(userId);
    const snap = await getDoc(progressRef);
    if (snap.exists()) {
      const { progress } = resolveStoryProgress(get().storyData, snap.data());
      applyResolvedProgress(set, get().storyData, progress);
      return progress;
    }

    return buildInitialStoryProgress(get().storyData);
  },

  _commitProgressUpdate: async (userId, updater) => {
    if (!userId) return null;

    const runUpdate = async () => {
      const storyData = get().storyData;
      const currentProgress = await get()._readProgressForMutation(userId);
      const nextProgress =
        typeof updater === "function"
          ? updater(currentProgress, storyData)
          : updater;

      if (!nextProgress || nextProgress === currentProgress) {
        return currentProgress;
      }

      await setDoc(
        storyProgressRefForUser(userId),
        sanitizeProgressForFirestore(nextProgress),
        {
          merge: true,
        },
      );

      applyResolvedProgress(set, storyData, nextProgress);

      return nextProgress;
    };

    const previousChain = get()._progressWriteChain || Promise.resolve();
    const nextChain = previousChain.catch(() => {}).then(runUpdate);
    set({ _progressWriteChain: nextChain });

    try {
      return await nextChain;
    } finally {
      if (get()._progressWriteChain === nextChain) {
        set({ _progressWriteChain: null });
      }
    }
  },

  saveProgress: async (userId, patch = {}) => {
    if (!userId) return null;

    return get()._commitProgressUpdate(userId, (currentProgress) => ({
      ...(currentProgress || buildInitialStoryProgress(get().storyData, patch)),
      ...patch,
    }));
  },

  ensureProgress: async (userId) => {
    if (!userId) return null;

    const currentProgress = get().progress;
    if (currentProgress) return currentProgress;

    const progressRef = storyProgressRefForUser(userId);
    const snap = await getDoc(progressRef);
    if (snap.exists()) {
      const { progress } = resolveStoryProgress(get().storyData, snap.data());
      applyResolvedProgress(set, get().storyData, progress);
      return progress;
    }

    const initialProgress = buildInitialStoryProgress(get().storyData);
    await get().saveProgress(userId, initialProgress);
    return initialProgress;
  },

  ensureStoryState: async (userId) => {
    if (!userId) return null;

    const currentStoryState = get().storyState;
    if (get().storyStateReady && currentStoryState) return currentStoryState;

    const stateRef = storyStateRefForUser(userId);
    const snap = await getDoc(stateRef);
    if (snap.exists()) {
      const didMigrateInventory = await migrateInlineInventoryToSubcollection(
        userId,
        snap.data(),
      );
      const resolvedState = resolveStoryState({
        ...snap.data(),
        inventoryCount: Array.isArray(snap.data()?.inventory)
          ? getInventoryTotal(normalizeStoryInventory(snap.data().inventory))
          : snap.data()?.inventoryCount,
      });
      set({
        storyState: resolvedState,
        storyStateReady: true,
      });

      if (needsStoryStateMerge(snap.data()) && !didMigrateInventory) {
        await setDoc(
          stateRef,
          {
            ...sanitizeStoryStateForFirestore(resolvedState),
            inventory: deleteField(),
          },
          {
            merge: true,
          },
        );
      }

      return resolvedState;
    }

    const initialStoryState = buildInitialStoryState();
    await get().saveStoryState(userId, initialStoryState);
    return initialStoryState;
  },

  saveStoryState: async (userId, patch = {}) => {
    if (!userId) return null;

    const currentStoryState = get().storyStateReady
      ? get().storyState
      : buildInitialStoryState();
    const nextStoryState = resolveStoryState({
      ...currentStoryState,
      ...patch,
    });

    await setDoc(
      storyStateRefForUser(userId),
      sanitizeStoryStateForFirestore(nextStoryState),
      {
        merge: true,
      },
    );

    set({
      storyState: nextStoryState,
      storyStateReady: true,
    });

    return nextStoryState;
  },

  grantStoryInventoryItem: async (userId, itemPatch = {}) => {
    if (!userId) return null;

    const normalizedItem = normalizeStoryInventoryEntry(itemPatch);
    const itemDocId = getStoryInventoryDocId(normalizedItem);
    if (!normalizedItem || !itemDocId) return null;

    await get().ensureStoryState(userId);

    const itemRef = storyInventoryItemRefForUser(userId, itemDocId);
    const stateRef = storyStateRefForUser(userId);
    const itemSnap = await getDoc(itemRef);
    const existingItem = itemSnap.exists()
      ? normalizeStoryInventoryEntry(itemSnap.data())
      : null;
    const existingQuantity = Number(existingItem?.quantity || 0);
    const nextQuantity = normalizedItem.stackable
      ? existingQuantity + normalizedItem.quantity
      : Math.max(existingQuantity, normalizedItem.quantity, 1);
    const quantityDelta = Math.max(0, nextQuantity - existingQuantity);
    const nextItem = {
      ...normalizedItem,
      quantity: nextQuantity,
      acquiredAt: existingItem?.acquiredAt ?? serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const nextInventoryCount = Math.max(
      0,
      Number(get().storyState?.inventoryCount || 0) + quantityDelta,
    );

    const batch = writeBatch(db);
    batch.set(itemRef, sanitizeStoryInventoryItemForFirestore(nextItem), {
      merge: true,
    });
    batch.set(
      stateRef,
      {
        inventoryCount: nextInventoryCount,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    await batch.commit();

    set((state) => ({
      storyState: {
        ...state.storyState,
        inventoryCount: nextInventoryCount,
      },
    }));

    return {
      ...normalizedItem,
      docId: itemDocId,
      quantity: nextQuantity,
    };
  },

  consumeStoryInventoryItem: async (userId, itemPatch = {}, quantity = 1) => {
    if (!userId) return null;

    const normalizedItem = normalizeStoryInventoryEntry(itemPatch);
    const itemDocId = getStoryInventoryDocId(normalizedItem);
    const quantityToConsume = Math.max(1, Math.floor(Number(quantity) || 1));
    if (!normalizedItem || !itemDocId) return null;

    await get().ensureStoryState(userId);

    const itemRef = storyInventoryItemRefForUser(userId, itemDocId);
    const stateRef = storyStateRefForUser(userId);
    const itemSnap = await getDoc(itemRef);
    if (!itemSnap.exists()) return null;

    const existingItem = normalizeStoryInventoryEntry(itemSnap.data());
    if (!existingItem) return null;

    const existingQuantity = Number(existingItem.quantity || 0);
    const nextQuantity = Math.max(0, existingQuantity - quantityToConsume);
    const quantityDelta = nextQuantity - existingQuantity;
    const nextInventoryCount = Math.max(
      0,
      Number(get().storyState?.inventoryCount || 0) + quantityDelta,
    );

    const batch = writeBatch(db);
    if (nextQuantity <= 0) {
      batch.delete(itemRef);
    } else {
      batch.set(
        itemRef,
        {
          quantity: nextQuantity,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
    batch.set(
      stateRef,
      {
        inventoryCount: nextInventoryCount,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    await batch.commit();

    set((state) => ({
      storyState: {
        ...state.storyState,
        inventoryCount: nextInventoryCount,
      },
    }));

    return nextQuantity <= 0
      ? null
      : {
          ...existingItem,
          docId: itemDocId,
          quantity: nextQuantity,
        };
  },

  transactStoryMerchantItem: async (
    userId,
    action = "",
    sourceId = "",
    quantity = 1,
  ) => {
    if (!userId) return null;

    const normalizedAction = String(action || "").trim().toLowerCase();
    const catalogItem = getStoryMerchantCatalogItem(sourceId);
    const tradeQuantity = Number(quantity);
    if (
      !catalogItem ||
      !["buy", "sell"].includes(normalizedAction) ||
      !Number.isInteger(tradeQuantity) ||
      tradeQuantity < 1 ||
      tradeQuantity > 99
    ) {
      throw createStoryMerchantError(
        "story-merchant/invalid-item",
        "That item or quantity is not available from this merchant.",
      );
    }

    const progressRef = storyProgressRefForUser(userId);
    const stateRef = storyStateRefForUser(userId);
    const normalizedCatalogItem = normalizeStoryInventoryEntry({
      ...catalogItem,
      quantity: 1,
      stackable: true,
    });
    const itemDocId = getStoryInventoryDocId(normalizedCatalogItem);
    const itemRef = storyInventoryItemRefForUser(userId, itemDocId);

    const result = await runTransaction(db, async (transaction) => {
      const [progressSnap, stateSnap, itemSnap] = await Promise.all([
        transaction.get(progressRef),
        transaction.get(stateRef),
        transaction.get(itemRef),
      ]);

      if (!progressSnap.exists() || !hasCompletedStoryMerchantUnlock(progressSnap.data())) {
        throw createStoryMerchantError(
          "story-merchant/locked",
          "Complete Act I, Mission 10 before trading at Merchant's Row.",
        );
      }

      const currentStoryState = resolveStoryState(
        stateSnap.exists() ? stateSnap.data() : buildInitialStoryState(),
      );
      const existingItem = itemSnap.exists()
        ? normalizeStoryInventoryEntry(itemSnap.data())
        : null;
      const existingQuantity = Number(existingItem?.quantity || 0);
      const currentInventoryCount = Number(currentStoryState.inventoryCount || 0);
      const currentBalance = Number(currentStoryState.infinityTokens || 0);

      let nextQuantity;
      let nextInventoryCount;
      let nextBalance;

      if (normalizedAction === "buy") {
        const totalPrice = catalogItem.price * tradeQuantity;
        if (currentBalance < totalPrice) {
          throw createStoryMerchantError(
            "story-merchant/insufficient-tokens",
            `You need ${totalPrice} Infinity Tokens to buy ${tradeQuantity} ${catalogItem.name}.`,
          );
        }

        nextQuantity = existingQuantity + tradeQuantity;
        nextInventoryCount = currentInventoryCount + tradeQuantity;
        nextBalance = currentBalance - totalPrice;
        transaction.set(
          itemRef,
          sanitizeStoryInventoryItemForFirestore({
            kind: "infinityCard",
            id: normalizedCatalogItem.id,
            sourceId: normalizedCatalogItem.sourceId,
            name: normalizedCatalogItem.name,
            imageUrl: normalizedCatalogItem.imageUrl || "",
            quantity: nextQuantity,
            stackable: true,
            acquiredAt: existingItem?.acquiredAt ?? serverTimestamp(),
            updatedAt: serverTimestamp(),
          }),
          { merge: true },
        );
      } else {
        if (!existingItem || existingQuantity < tradeQuantity) {
          throw createStoryMerchantError(
            "story-merchant/item-not-owned",
            `You do not have ${tradeQuantity} ${catalogItem.name} to sell.`,
          );
        }

        nextQuantity = existingQuantity - tradeQuantity;
        nextInventoryCount = Math.max(
          0,
          currentInventoryCount - tradeQuantity,
        );
        nextBalance = currentBalance + catalogItem.sellPrice * tradeQuantity;
        if (nextQuantity <= 0) {
          transaction.delete(itemRef);
        } else {
          transaction.set(
            itemRef,
            {
              quantity: nextQuantity,
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          );
        }
      }

      transaction.set(
        stateRef,
        {
          infinityTokens: nextBalance,
          inventoryCount: nextInventoryCount,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      return {
        action: normalizedAction,
        item: catalogItem,
        inventoryItem: normalizedCatalogItem,
        itemDocId,
        tradeQuantity,
        tokenTotal:
          (normalizedAction === "sell"
            ? catalogItem.sellPrice
            : catalogItem.price) * tradeQuantity,
        quantity: nextQuantity,
        infinityTokens: nextBalance,
        inventoryCount: nextInventoryCount,
      };
    });

    set((state) => {
      const existingIndex = state.storyInventory.findIndex(
        (item) => item.docId === result.itemDocId,
      );
      const nextInventory = [...state.storyInventory];

      if (result.quantity <= 0) {
        if (existingIndex >= 0) nextInventory.splice(existingIndex, 1);
      } else {
        const nextItem = {
          ...(existingIndex >= 0 ? nextInventory[existingIndex] : {}),
          kind: "infinityCard",
          id: result.inventoryItem.id,
          sourceId: result.inventoryItem.sourceId,
          name: result.inventoryItem.name,
          imageUrl: result.inventoryItem.imageUrl || "",
          quantity: result.quantity,
          stackable: true,
          docId: result.itemDocId,
        };
        if (existingIndex >= 0) nextInventory[existingIndex] = nextItem;
        else nextInventory.push(nextItem);
      }

      return {
        storyInventory: nextInventory,
        storyState: {
          ...state.storyState,
          infinityTokens: result.infinityTokens,
          inventoryCount: result.inventoryCount,
        },
      };
    });

    return result;
  },

  advance: async (userId) => {
    return get()._commitProgressUpdate(userId, (progress, storyData) =>
      advanceStoryProgress(storyData, progress),
    );
  },

  setLaunchTarget: async (userId, targetPatch = {}) => {
    return get()._commitProgressUpdate(userId, (progress, storyData) =>
      withStoryLaunchTarget(storyData, progress, targetPatch),
    );
  },

  setCutsceneProgress: async (userId, cutsceneId, patch = {}) => {
    if (!cutsceneId) return null;

    return get()._commitProgressUpdate(userId, (progress, storyData) => {
      const activeCutsceneId =
        progress?.currentLaunchTarget?.kind === STORY_TARGET_KINDS.CUTSCENE
          ? progress.currentLaunchTarget.cutsceneId
          : null;

      if (activeCutsceneId !== cutsceneId) {
        return progress;
      }

      return withStoryCutsceneProgress(storyData, progress, cutsceneId, patch);
    });
  },

  setMissionById: async (userId, missionId, stepIndex = 0) => {
    return get()._commitProgressUpdate(userId, (progress, storyData) =>
      buildInitialStoryProgress(storyData, {
        currentMissionId: missionId,
        currentStepIndex: stepIndex,
        startedAt: progress?.startedAt,
        lastCompletedMissionId: progress?.lastCompletedMissionId || null,
      }),
    );
  },
}));
